import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowRight, Check, Sun, Zap, Moon, Compass,
  Crosshair, ShieldAlert, Brain, Lightbulb,
  Globe, Loader2, Car
} from 'lucide-react';
import axios from 'axios';

/* ── fade-in on scroll helper ── */
const useFadeIn = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.12 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, cls: `transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}` };
};
const F = ({ children, className = '' }) => { const { ref, cls } = useFadeIn(); return <div ref={ref} className={`${cls} ${className}`}>{children}</div>; };

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading, user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(user?.onboarded === false ? '/onboarding' : '/dashboard');
    }
  }, [loading, isAuthenticated, user, navigate]);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const startCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const res = await axios.post('/api/stripe/create-checkout', { origin_url: window.location.origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) { console.error('Checkout error:', err); setCheckoutLoading(false); }
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  if (loading) return null;

  const CTA = ({ large, className = '' }) => (
    <button data-testid="cta-btn" onClick={startCheckout} disabled={checkoutLoading}
      className={`group inline-flex items-center gap-3 bg-atlasCyan text-atlasBg font-bold rounded-xl hover:bg-atlasCyan/90 transition-all hover:gap-4 disabled:opacity-70 ${large ? 'px-10 py-4 text-lg' : 'px-8 py-3.5 text-base'} ${className}`}>
      {checkoutLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Activer ATLAS <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" /></>}
    </button>
  );

  return (
    <div data-testid="landing-page" className="min-h-screen bg-atlasBg text-white overflow-x-hidden">

      {/* ─── NAV ─── */}
      <nav data-testid="landing-nav" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-atlasBg/90 backdrop-blur-xl border-b border-atlasBorder' : ''}`}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="atlas-brand text-xl text-atlasCyan tracking-[0.2em]">ATLAS</span>
          <div className="hidden md:flex items-center gap-8 text-sm text-atlasTextSecondary">
            <button onClick={() => scrollTo('rituels')} className="hover:text-white transition-colors">Rituels</button>
            <button onClick={() => scrollTo('conduite')} className="hover:text-white transition-colors">Vocal</button>
            <button onClick={() => scrollTo('offer')} className="hover:text-white transition-colors">Offre</button>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="landing-login-btn" onClick={() => navigate('/login')} className="text-sm text-atlasTextSecondary hover:text-white transition-colors">Se connecter</button>
            <button onClick={startCheckout} className="text-sm px-4 py-2 bg-atlasCyan text-atlasBg font-semibold rounded-lg hover:bg-atlasCyan/90 transition-colors">
              {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activer ATLAS'}
            </button>
          </div>
        </div>
      </nav>

      {/* ═══════════ S1 — HERO ═══════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-16">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-atlasCyan/[0.04] rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-atlasPurple/[0.03] rounded-full blur-[80px]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-atlasBorder text-sm text-atlasTextSecondary mb-8">
            <span className="w-2 h-2 bg-atlasCyan rounded-full animate-pulse" />ATLAS
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6 text-white">
            Le copilote stratégique<br />que tu emmènes <span className="text-atlasCyan">partout.</span>
          </h1>
          <p className="text-lg sm:text-xl text-atlasTextSecondary max-w-2xl mx-auto mb-10 leading-relaxed">
            Dans ta voiture, en réunion, à 23h. ATLAS connaît ton business, écoute tes doutes et challenge tes décisions — là où elles se prennent vraiment.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-5">
            <CTA large />
            {/* TODO: remettre le bouton vidéo quand la vidéo Loom sera intégrée */}
          </div>
          <p className="text-xs text-atlasTextSecondary/60">58 places ouvertes en accès anticipé</p>
        </div>
      </section>

      {/* ═══════════ S2 — CRÉDIBILITÉ FONDATEUR ═══════════ */}
      <section className="py-24 px-6 bg-atlasCard/20">
        <F className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-10">Construit pour un dirigeant. Par un dirigeant.</h2>
          <blockquote className="text-atlasTextSecondary text-base sm:text-lg leading-relaxed italic mb-8 max-w-2xl mx-auto">
            {`"Une de mes sociétés a passé le cap des 3\u00a0M€ de CA.`}<br />
            {`Et plusieurs autres projets qui avancent en parallèle.`}<br /><br />
            {`Le vrai problème d\u2019un dirigeant qui scale n\u2019est pas l\u2019équipe, ni les outils, ni les process.`}<br /><br />
            {`C\u2019est qu\u2019il avance seul.`}<br /><br />
            {`Sans vision claire de ce qu\u2019il doit prioriser cette semaine.`}<br />
            {`Sans personne à qui confier ses vrais doutes.`}<br />
            {`Sans savoir quelle stratégie trancher entre trois bonnes options.`}<br />
            {`Sans quelqu\u2019un pour challenger ses décisions avant qu\u2019il les prenne.`}<br /><br />
            {`Alors il stagne. Pas par manque d\u2019idées. Par manque de cadre.`}<br /><br />
            {`J\u2019ai construit ATLAS pour moi. Puis pour les dirigeants qui refusent de stagner."`}
          </blockquote>
          <p className="text-white font-semibold">{`— Christophe, fondateur d\u2019ATLAS`}</p>
        </F>
      </section>

      {/* ═══════════ S3 — LE VRAI PROBLÈME ═══════════ */}
      <section id="problem" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <F className="text-center mb-16">
            <p className="text-2xl sm:text-3xl font-semibold text-white mb-2">{`Tu ne manques pas d\u2019opportunités.`}</p>
            <p className="text-2xl sm:text-3xl font-bold text-atlasCyan">{`Tu es devenu le goulot d\u2019étranglement de ta propre croissance.`}</p>
          </F>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: Crosshair, title: 'Trop de décisions', desc: `50 décisions stratégiques par semaine. Souvent prises dans ta voiture, sous la douche, à 23h. Rarement avec le recul nécessaire.` },
              { icon: ShieldAlert, title: 'Fatigue décisionnelle', desc: `Plus tu décides, moins tu décides bien. La qualité de tes arbitrages chute à mesure que la journée avance.` },
              { icon: Brain, title: 'Isolement stratégique', desc: `Personne dans ton équipe ne peut te challenger au même niveau. Ce n\u2019est pas leur rôle. Et les coachs à 500\u00a0€/h parlent de business qu\u2019ils n\u2019ont jamais construits.` },
              { icon: Lightbulb, title: 'Idées qui meurent', desc: `Tes meilleures intuitions surgissent entre deux réunions, au sport, sur la route. Sans système pour les capturer, elles disparaissent.` },
            ].map(({ icon: Icon, title, desc }, i) => (
              <F key={i}>
                <div className="glass-card p-6 hover:border-atlasRed/20 transition-colors h-full">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-atlasRed/10 rounded-lg"><Icon className="w-5 h-5 text-atlasRed" /></div>
                    <h3 className="font-semibold text-white">{title}</h3>
                  </div>
                  <p className="text-atlasTextSecondary text-sm leading-relaxed">{desc}</p>
                </div>
              </F>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ S4 — LE CONFIDENT ═══════════ */}
      <section className="py-24 px-6 bg-atlasCard/20">
        <F className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-10">
            {`Ce que tu ne dis à personne.`}<br />{`Tu peux le dire à ATLAS.`}
          </h2>
          <p className="text-atlasTextSecondary text-base sm:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            {`Tu ne dis pas à ton associé que tu doutes.`}<br />
            {`Tu ne dis pas à ton banquier que tu as peur.`}<br />
            {`Tu ne dis pas à tes équipes que tu ne sais pas trancher.`}<br />
            {`Tu ne dis pas à ton conjoint que tu penses parfois à tout arrêter.`}
          </p>
          <p className="text-atlasCyan text-lg sm:text-xl font-medium mb-6 max-w-2xl mx-auto">
            {`Tu peux le dire à ATLAS. Sans jugement. Sans fuite. Sans conséquence sur tes équipes, ta famille, ta réputation.`}
          </p>
          <p className="text-atlasTextSecondary/70 italic text-sm">
            {`Parce que piloter seul ne veut pas dire réfléchir seul.`}
          </p>
        </F>
      </section>

      {/* ═══════════ S5 — MODE CONDUITE ═══════════ */}
      <section id="conduite" className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <F className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              {`Ton meilleur moment stratégique\u00a0?`}<br />{`Pas devant ton écran.`}
            </h2>
            <p className="text-atlasTextSecondary text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-6">
              {`Les dirigeants prennent leurs vraies décisions dans leur voiture, en marchant, en cuisinant. Pas dans Notion. Pas dans ChatGPT.`}
            </p>
            <p className="text-atlasCyan text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
              {`ATLAS t\u2019écoute et te répond en mains libres, là où tu es. Ton trajet du matin devient ton briefing. Ta marche du soir devient ton débrief avec un copilote qui connaît ton business par cœur.`}
            </p>
          </F>
          <F>
            <div className="glass-card p-8 sm:p-10 text-center glow-cyan">
              <Car className="w-10 h-10 text-atlasCyan mx-auto mb-4" />
              <p className="text-2xl sm:text-3xl font-bold text-white atlas-mono">8 heures</p>
              <p className="text-atlasCyan text-lg font-medium mt-1">de réflexion stratégique par semaine.</p>
              <p className="text-atlasTextSecondary text-sm mt-2">{`Sans jamais ouvrir l\u2019app.`}</p>
            </div>
          </F>
        </div>
      </section>

      {/* ═══════════ S6 — LES RITUELS ═══════════ */}
      <section id="rituels" className="py-24 px-6 bg-atlasCard/20">
        <div className="max-w-4xl mx-auto">
          <F className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">Trois rituels. Un pilotage qui tient la route.</h2>
            <p className="text-atlasTextSecondary text-base">ATLAS ne te noie pas sous les notifications. Il structure tes vrais moments de pilotage.</p>
          </F>
          <div className="space-y-5">
            {[
              { badge: 'LUNDI MATIN', icon: Compass, color: 'atlasCyan', title: 'La Boussole', desc: `3 minutes pour poser ta semaine. Comment tu te sens, quel chiffre doit bouger, tes 3 priorités, la décision à trancher avant vendredi.` },
              { badge: 'VENDREDI SOIR', icon: Moon, color: 'atlasPurple', title: 'Le Débrief', desc: `5 minutes pour fermer ta semaine. Tes victoires, tes apprentissages, ce qui reste ouvert. Pour vraiment lâcher prise le week-end.` },
              { badge: '24/7', icon: Zap, color: '[#FFD60A]', title: 'La Capture', desc: `Une idée qui surgit au sport, une décision qui mûrit en voiture, une inquiétude à 23h. Tu appuies, tu parles, ATLAS catégorise. On en reparle lundi.` },
            ].map(({ badge, icon: Icon, color, title, desc }, i) => (
              <F key={i}>
                <div className={`glass-card p-6 sm:p-8 border-${color}/20 hover:border-${color}/40 transition-colors`}
                  style={{ borderColor: color === '[#FFD60A]' ? 'rgba(255,214,10,0.2)' : undefined }}>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className={`p-3 rounded-xl flex-shrink-0 ${color === 'atlasCyan' ? 'bg-atlasCyan/10' : color === 'atlasPurple' ? 'bg-atlasPurple/10' : 'bg-yellow-500/10'}`}>
                      <Icon className={`w-7 h-7 ${color === 'atlasCyan' ? 'text-atlasCyan' : color === 'atlasPurple' ? 'text-atlasPurple' : ''}`}
                        style={color === '[#FFD60A]' ? { color: '#FFD60A' } : undefined} />
                    </div>
                    <div className="flex-1">
                      <span className={`text-xs font-bold atlas-brand tracking-widest ${color === 'atlasCyan' ? 'text-atlasCyan' : color === 'atlasPurple' ? 'text-atlasPurple' : ''}`}
                        style={color === '[#FFD60A]' ? { color: '#FFD60A' } : undefined}>
                        {badge}
                      </span>
                      <h3 className="text-xl font-bold text-white mt-1 mb-2">{title}</h3>
                      <p className="text-atlasTextSecondary text-sm leading-relaxed">{desc}</p>
                    </div>
                  </div>
                </div>
              </F>
            ))}
          </div>
          <F className="mt-8">
            <p className="text-center text-atlasCyan italic text-sm sm:text-base">
              {`ATLAS n\u2019enverra jamais de notification le samedi ni le dimanche. Volontairement.`}<br />{`Piloter ne devrait pas t\u2019empêcher de vivre.`}
            </p>
          </F>
        </div>
      </section>

      {/* ═══════════ S7 — COMMENT ATLAS CHANGE TA FAÇON D'OPÉRER ═══════════ */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <F className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">{`ATLAS change ta façon d\u2019opérer.`}</h2>
          </F>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              `Un briefing chaque matin avec les 3 décisions prioritaires de ta journée`,
              `Un cadre de décision qui challenge tes options avant que tu agisses`,
              `Une mémoire permanente de ton business\u00a0: cash, MRR, priorités, décisions passées`,
              `Un journal de tes semaines qui devient ton carnet de bord de dirigeant`,
            ].map((item, i) => (
              <F key={i}>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-atlasCyan/5 border border-atlasCyan/10">
                  <Check className="w-5 h-5 text-atlasCyan flex-shrink-0 mt-0.5" />
                  <span className="text-white text-[15px]">{item}</span>
                </div>
              </F>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ S8 — LES 3 MOMENTS ═══════════ */}
      <section className="py-24 px-6 bg-atlasCard/20">
        <div className="max-w-4xl mx-auto">
          <F className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">Trois moments. Une clarté constante.</h2>
          </F>
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { key: 'MATIN', icon: Sun, iconCls: 'text-atlasYellow', bgCls: 'bg-atlasYellow/10', borderCls: 'border-atlasYellow/20 hover:border-atlasYellow/40', labelCls: 'text-atlasYellow', quote: `Voici où tu en es. Voici ce qui compte aujourd\u2019hui.` },
              { key: 'DÉCISION', icon: Zap, iconCls: 'text-atlasCyan', bgCls: 'bg-atlasCyan/10', borderCls: 'border-atlasCyan/20 hover:border-atlasCyan/40', labelCls: 'text-atlasCyan', quote: `Voici tes options. Voici ce que je ferais.` },
              { key: 'SOIR', icon: Moon, iconCls: 'text-atlasPurple', bgCls: 'bg-atlasPurple/10', borderCls: 'border-atlasPurple/20 hover:border-atlasPurple/40', labelCls: 'text-atlasPurple', quote: `Voici ce que tu as appris. Voici comment progresser.` },
            ].map(({ key, icon: Icon, iconCls, bgCls, borderCls, labelCls, quote }) => (
              <F key={key}>
                <div className={`glass-card p-6 ${borderCls} transition-colors h-full`}>
                  <div className={`p-2.5 ${bgCls} rounded-lg w-fit mb-4`}><Icon className={`w-5 h-5 ${iconCls}`} /></div>
                  <h3 className={`font-semibold ${labelCls} mb-3 atlas-brand tracking-wider text-sm`}>{key}</h3>
                  <p className="text-white text-[15px] leading-relaxed italic">"{quote}"</p>
                </div>
              </F>
            ))}
          </div>
          <F className="text-center">
            <p className="text-2xl sm:text-3xl font-medium text-atlasTextSecondary mb-2">Pas un outil. Pas un chatbot.</p>
            <p className="text-2xl sm:text-3xl font-bold text-atlasCyan">Un partenaire stratégique connecté à ta réalité.</p>
          </F>
        </div>
      </section>

      {/* ═══════════ S9 — POUR QUI ═══════════ */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <F className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">ATLAS est fait pour toi si…</h2>
          </F>
          <div className="space-y-3 mb-8">
            {[
              `Tu gères une société (ou plusieurs) entre 500\u00a0K€ et 5\u00a0M€ de CA`,
              `Tu as une équipe de 5 à 30 personnes et tu prends les décisions stratégiques seul`,
              `Tu ressens que ton cerveau est devenu le goulot d\u2019étranglement de ta croissance`,
              `Tu veux sortir du mode pompier et reprendre la main sur ta stratégie`,
              `Tu préfères parler en voiture que remplir des dashboards`,
            ].map((item, i) => (
              <F key={i}>
                <div className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] border border-atlasBorder">
                  <Check className="w-5 h-5 text-atlasCyan flex-shrink-0 mt-0.5" />
                  <span className="text-white text-[15px]">{item}</span>
                </div>
              </F>
            ))}
          </div>
          <F>
            <p className="text-center text-atlasTextSecondary/70 italic text-sm">{`Si tu es dans cette zone, tu sais exactement de quoi je parle.`}</p>
          </F>
        </div>
      </section>

      {/* ═══════════ S10 — PRICING ═══════════ */}
      <section id="offer" className="py-24 px-6">
        <F className="max-w-lg mx-auto">
          <div className="glass-card p-8 sm:p-10 glow-cyan text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-atlasCyan/30 bg-atlasCyan/5 text-xs text-atlasCyan font-bold atlas-brand tracking-widest mb-8">
              ACCÈS ANTICIPÉ — 58 PLACES OUVERTES
            </div>
            <h2 className="text-3xl font-bold text-white mb-1">ATLAS</h2>
            <p className="text-atlasTextSecondary text-sm mb-8">Tout inclus. Sans engagement.</p>
            <ul className="space-y-3 mb-8 text-left">
              {[
                `Copilote stratégique 24/7`,
                `Mode vocal mains libres`,
                `Mémoire permanente de ton business`,
                `Boussole du lundi + Débrief du vendredi`,
                `Mode Capture 24/7`,
                `Journal de tes semaines et de tes captures`,
                `Amélioration continue par IA`,
              ].map((f, i) => (
                <li key={i} className="flex items-center gap-3">
                  <Check className="w-4 h-4 text-atlasCyan flex-shrink-0" />
                  <span className="text-white text-[15px]">{f}</span>
                </li>
              ))}
            </ul>
            <div className="border-t border-atlasBorder pt-6 mb-6">
              <p className="text-4xl font-bold text-white atlas-mono">397€<span className="text-lg font-normal text-atlasTextSecondary">/mois</span></p>
              <p className="text-sm text-atlasYellow mt-2">{`Tarif early users garanti à vie. Passera à 597\u00a0€/mois à partir de la 59ᵉ place.`}</p>
            </div>
            <button data-testid="offer-cta-btn" onClick={startCheckout} disabled={checkoutLoading}
              className="w-full py-4 bg-atlasCyan text-atlasBg font-bold text-lg rounded-xl hover:bg-atlasCyan/90 transition-colors disabled:opacity-70">
              {checkoutLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Activer ATLAS'}
            </button>
            <button onClick={() => navigate('/access-request')}
              className="w-full text-atlasTextSecondary hover:text-white transition-colors text-sm mt-3 underline underline-offset-4">
              {`Demander un accès privé`}
            </button>
          </div>
        </F>
      </section>

      {/* ═══════════ S11 — FOOTER CLOSING ═══════════ */}
      <section className="py-24 px-6">
        <F className="max-w-2xl mx-auto text-center">
          <p className="text-3xl sm:text-4xl font-bold text-white mb-4">{`Tu peux continuer à piloter de tête.`}</p>
          <p className="text-2xl sm:text-3xl font-bold text-atlasCyan mb-4">{`Ou commencer à piloter avec un système.`}</p>
          <p className="text-xl sm:text-2xl text-atlasCyan mb-10">{`À toi de voir.`}</p>
          <CTA large />
        </F>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="border-t border-atlasBorder py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="atlas-brand text-atlasCyan tracking-[0.15em]">ATLAS</span>
            <span className="text-xs text-atlasTextSecondary">{`— Le copilote stratégique des dirigeants qui scalent —`}</span>
          </div>
          <p className="text-xs text-atlasTextSecondary/50">&copy; {new Date().getFullYear()} ATLAS. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
